import { useState } from 'react';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9q2uq4nlsngrm4llqk0';

export default function ContactInfo() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hpField = formData.get('phone_alt') as string;
    if (hpField && hpField.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    const textarea = form.querySelector('textarea') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      setFormError('留言内容不能超过500个字符');
      setFormStatus('error');
      return;
    }

    setFormStatus('loading');
    try {
      const payload = new URLSearchParams();
      payload.append('name', (formData.get('name') as string) || '');
      payload.append('email', (formData.get('email') as string) || '');
      payload.append('subject', (formData.get('subject') as string) || '');
      payload.append('message', (formData.get('message') as string) || '');
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed && (parsed as Record<string, unknown>).meta && ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message)
          || (parsed && (parsed as Record<string, unknown>).message)
          || responseText
          || '发送失败，请稍后重试';
        setFormError(typeof serverMsg === 'string' ? serverMsg : '发送失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <section className="w-full py-14 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-10 md:gap-14">
          <div className="w-full lg:w-2/5">
            <span className="inline-block px-3 py-1.5 rounded-md text-xs font-medium bg-secondary-100 text-secondary-700 mb-4">
              联系我们
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              期待与你相遇
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
              有任何问题、建议或合作意向，欢迎随时与我们联系。我们会在48小时内回复你的消息。
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-mail-line text-lg text-primary-600"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</div>
                  <div className="text-sm text-foreground-600">contact@yuriclub.cn</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-map-pin-line text-lg text-accent-600"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground-950 mb-1">办公地址</div>
                  <div className="text-sm text-foreground-600">北京市朝阳区百合大道100号</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-time-line text-lg text-secondary-600"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground-950 mb-1">工作时间</div>
                  <div className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-3/5">
            <div className="p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-6">
                发送消息
              </h3>
              {formStatus === 'success' ? (
                <div className="flex items-center justify-center gap-2 text-accent-700 bg-accent-100/80 py-4 px-6 rounded-xl">
                  <i className="ri-checkbox-circle-line text-xl"></i>
                  <span className="text-sm font-medium">消息已发送！我们会尽快回复你</span>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form="百合会联系我们"
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-300/60 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                        placeholder="你的名字"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-300/60 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="contact-subject"
                      type="text"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-300/60 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all"
                      placeholder="消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-foreground-400 font-normal">（500字以内）</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-300/60 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-all resize-none"
                      placeholder="请输入你的留言内容..."
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />
                  {formStatus === 'error' && (
                    <p className="text-sm text-secondary-600">{formError}</p>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 rounded-lg text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}